import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import Logo from '../../common/Logo';
import LanguagePicker from '../../Helpers/Selectbox/LanguagePicker';
import { ChevronRight, Close, Mail } from '../../Helpers/icons/Icons';

type PropsType = {
  className?: string;
  open: boolean;
  action: () => void;
  events: {
    id: number;
    name: string;
  }[];
};

const DrawerLink = ({
  to,
  children,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `flex h-12 items-center justify-between gap-3 rounded-xl px-3 text-[15px] transition-colors ${
        isActive ? 'bg-canvas font-semibold text-ink-900' : 'text-ink-700 hover:bg-canvas'
      }`
    }
  >
    <span>{children}</span>
    <ChevronRight size={15} className='text-ink-300' />
  </NavLink>
);

export default function Drawer({ className, open, action, events }: PropsType) {
  const [tab, setTab] = useState<'menu' | 'events'>('menu');
  const { t } = useTranslation();

  /* Stop the page behind the drawer from scrolling while it is open. */
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && action();
    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [open, action]);

  const menu = [
    { to: '/', label: t('home') },
    { to: '/products', label: t('gift_cards') },
    { to: '/companies', label: t('companies') },
    { to: '/blog', label: t('blog') },
    { to: '/about', label: t('about_us') },
    { to: '/contact', label: t('contact') },
  ];

  const partner = [
    { to: '/become-seller', label: t('become_a_partner') },
    { to: '/request-company', label: t('request_company') },
  ];

  return (
    <div className={`drawer-wrapper xl:hidden ${className || ''}`}>
      <div
        onClick={action}
        aria-hidden='true'
        className={`fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-[2px] transition-opacity duration-300 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      <aside
        aria-label={`${t('menu')}`}
        aria-hidden={!open}
        className={`overflow-style-none fixed top-0 z-50 flex h-[100dvh] w-[310px] max-w-[86vw] flex-col
          bg-paper transition-transform duration-300 ease-gift ${
            open ? 'translate-x-0' : '-translate-x-full'
          } left-0`}
      >
        <div className='flex items-center justify-between border-b border-line px-4 py-3'>
          <Logo size={34} to='/' />
          <button
            type='button'
            onClick={action}
            aria-label={`${t('close')}`}
            className='flex h-9 w-9 items-center justify-center rounded-pill text-ink-600 transition-colors hover:bg-canvas'
          >
            <Close size={19} />
          </button>
        </div>

        <div className='flex gap-1 border-b border-line px-4 pt-3'>
          {(['menu', 'events'] as const).map((key) => (
            <button
              key={key}
              type='button'
              onClick={() => setTab(key)}
              className={`relative pb-3 pr-5 text-sm font-semibold transition-colors ${
                tab === key ? 'text-ink-900' : 'text-ink-400'
              }`}
            >
              {key === 'menu' ? t('main_menu') : t('event')}
              {tab === key && (
                <span className='absolute bottom-0 left-0 h-[2px] w-[calc(100%-20px)] bg-apricot-500' />
              )}
            </button>
          ))}
        </div>

        <nav className='flex-1 overflow-y-auto p-3'>
          {tab === 'menu' ? (
            <>
              <div className='flex flex-col gap-0.5'>
                {menu.map((item) => (
                  <DrawerLink key={item.to} to={item.to} onClick={action}>
                    {item.label}
                  </DrawerLink>
                ))}
              </div>
              <p className='eyebrow mt-6 mb-2 px-3'>{t('partnership')}</p>
              <div className='flex flex-col gap-0.5'>
                {partner.map((item) => (
                  <DrawerLink key={item.to} to={item.to} onClick={action}>
                    {item.label}
                  </DrawerLink>
                ))}
              </div>
            </>
          ) : (
            <div className='flex flex-col gap-0.5'>
              {events.map((item) => (
                <Link
                  key={item.id}
                  to={`/products?events=${item.id}`}
                  onClick={action}
                  className='flex h-12 items-center justify-between gap-3 rounded-xl px-3 text-[15px] text-ink-700 transition-colors hover:bg-canvas'
                >
                  <span>{item.name}</span>
                  <ChevronRight size={15} className='text-ink-300' />
                </Link>
              ))}
            </div>
          )}
        </nav>

        <div className='border-t border-line p-4'>
          <div className='mb-3 flex flex-col gap-2 text-[13px] text-ink-500'>
            <a
              href='mailto:info@egiftcards.am'
              className='flex items-center gap-2 hover:text-ink-900'
            >
              <Mail size={14} />
              info@egiftcards.am
            </a>
          </div>
          <div className='-ml-2'>
            <LanguagePicker tone='light' />
          </div>
        </div>
      </aside>
    </div>
  );
}
